import { Component, computed, inject, signal, input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import {Router, ActivatedRoute} from '@angular/router';
import {
  ImageElement,
  LabelConfig,
  LabelElement,
  TaggingLabelCanvasComponent,
  TextElement,
  WebSafeFont
} from './tagging-label-canvas.component';
import { ButtonComponent } from '../button/button.component';
import { AlertComponent } from '../alert/alert.component';
import {
  AttachmentsService,
  MAX_ATTACHMENT_SIZE_BYTES,
} from '../../../core/services/attachments/attachments.service';
import { TagsService } from '../../../core/services/tagging/tags.service';
import { ProductTagTypeCrudCreateModel, ProductTagTypeCrudUpdateModel } from '../../../core/models/tagging/product-tag-type.model';

function uid(): string {
  return crypto.randomUUID();
}

@Component({
  selector: 'app-tagging-label-builder',
  imports: [CommonModule, TaggingLabelCanvasComponent, ButtonComponent, AlertComponent, FormsModule, ReactiveFormsModule],
  templateUrl: './tagging-label-builder.component.html',
  styleUrl: './tagging-label-builder.component.scss',
  standalone: true
})
export class TaggingLabelBuilderComponent implements OnInit {
  private readonly attachmentsService = inject(AttachmentsService);
  private readonly tagsService = inject(TagsService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly fb = inject(FormBuilder);

  readonly id = signal<number | null>(null);

  form = this.fb.group({
    name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(64)]],
    description: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(255)]]
  });

  alertMessage = signal<string | null>(null);
  alertType = signal<'success' | 'danger' | 'warning' | 'info'>('info');
  isSaving = signal(false);
  isUploading = signal(false);

  readonly maxFileSizeMb = MAX_ATTACHMENT_SIZE_BYTES / (1024 * 1024);

  fonts: WebSafeFont[] = [
    WebSafeFont.Arial,
    WebSafeFont.TimesNewRoman,
    WebSafeFont.CourierNew,
    WebSafeFont.TrebuchetMS,
    WebSafeFont.LucidaConsole,
    WebSafeFont.Georgia,
    WebSafeFont.Tahoma,
    WebSafeFont.Verdana
  ];

  label = signal<LabelConfig>({
    widthMm: 80,
    heightMm: 50,
    backgroundColor: '#ffffff',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
  });

  elements = signal<LabelElement[]>([]);
  selectedId = signal<string | null>(null);
  showCenterGuide = signal(false);

  selected = computed(() => {
    const id = this.selectedId();
    return id ? this.elements().find(e => e.id === id) ?? null : null;
  });

  setLabel(patch: Partial<LabelConfig>) {
    // Arredonda valores numéricos se existirem no patch
    const roundedPatch = {...patch};
    if (roundedPatch.widthMm !== undefined) roundedPatch.widthMm = Math.round(roundedPatch.widthMm);
    if (roundedPatch.heightMm !== undefined) roundedPatch.heightMm = Math.round(roundedPatch.heightMm);

    // Se estiver definindo uma imagem de fundo, garante que o canvas atualize limpando o estilo antigo se necessário
    this.label.update(v => ({...v, ...roundedPatch}));
  }

  addText() {
    const el: TextElement = {
      id: uid(),
      name: 'Texto ' + (this.elements().length + 1),
      type: 'text',
      xMm: 5, yMm: 5, wMm: 40, hMm: 15,
      zIndex: this.getNextZIndex(),
      text: 'Texto',
      color: '#000000',
      fontFamily: WebSafeFont.Arial,
      fontSizePt: 12,
      textAlign: 'left',
      fontWeight: 'normal',
      fontStyle: 'normal',
      textDecoration: 'none',
      letterSpacingPx: 0,
      lineHeight: 1.2,
    };
    this.elements.update(list => [...list, el]);
    this.selectedId.set(el.id);
  }

  addImage() {
    const el: ImageElement = {
      id: uid(),
      name: 'Imagem ' + (this.elements().length + 1),
      type: 'image',
      xMm: 5, yMm: 25, wMm: 30, hMm: 20,
      zIndex: this.getNextZIndex(),
      src: 'https://via.placeholder.com/300x200.png?text=Image',
      objectFit: 'contain'
    };
    this.elements.update(list => [...list, el]);
    this.selectedId.set(el.id);
  }

  private getNextZIndex(): number {
    const list = this.elements();
    if (list.length === 0) return 10;
    return Math.max(...list.map(e => e.zIndex)) + 1;
  }

  layers = computed(() => {
    return [...this.elements()].sort((a, b) => b.zIndex - a.zIndex);
  });

  moveLayer(id: string, direction: 'up' | 'down') {
    const list = [...this.elements()];
    const sorted = [...list].sort((a, b) => a.zIndex - b.zIndex);
    const sIndex = sorted.findIndex(e => e.id === id);
    if (sIndex === -1) return;

    if (direction === 'up' && sIndex < sorted.length - 1) {
      [sorted[sIndex], sorted[sIndex + 1]] = [sorted[sIndex + 1], sorted[sIndex]];
    } else if (direction === 'down' && sIndex > 0) {
      [sorted[sIndex], sorted[sIndex - 1]] = [sorted[sIndex - 1], sorted[sIndex]];
    } else {
      return;
    }

    sorted.forEach((el, i) => {
      el.zIndex = (i + 1) * 10;
    });

    this.elements.set([...sorted]);
  }

  updateElement(patch: Partial<LabelElement> & { id: string }) {
    // Arredonda valores numéricos se existirem no patch
    const roundedPatch = {...patch} as any;
    ['xMm', 'yMm', 'wMm', 'hMm'].forEach(key => {
      if (roundedPatch[key] !== undefined) roundedPatch[key] = Number(roundedPatch[key].toFixed(1));
    });

    this.elements.update(list =>
      list.map(e => e.id === roundedPatch.id ? ({...e, ...roundedPatch} as any) : e)
    );
  }

  patchSelected(patch: any) {
    const s = this.selected();
    if (!s) return;
    this.updateElement({id: s.id, ...patch});
  }

  onImageUpload(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    this.uploadImageFile(file, (url) => this.patchSelected({ src: url }));
    (event.target as HTMLInputElement).value = '';
  }

  onBackgroundImageUpload(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    this.uploadImageFile(file, (url) => this.setLabel({ backgroundImageUrl: url }));
    (event.target as HTMLInputElement).value = '';
  }

  private uploadImageFile(
    file: File,
    onSuccess: (url: string) => void,
    onError?: (msg: string) => void
  ) {
    if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
      const msg = 'O arquivo excede o limite de 5MB.';
      if (onError) onError(msg);
      else this.showAlert(msg, 'danger');
      return;
    }
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      const msg = 'Aceito apenas JPG ou PNG.';
      if (onError) onError(msg);
      else this.showAlert(msg, 'danger');
      return;
    }

    const baseName = file.name.replace(/\.[^.]+$/, '') || 'image';
    this.isUploading.set(true);
    this.attachmentsService.upload(file, baseName).subscribe({
      next: (res) => {
        onSuccess(res.url);
        this.isUploading.set(false);
      },
      error: (err) => {
        const msg = err?.error?.message ?? err?.message ?? 'Falha no upload.';
        if (onError) onError(msg);
        else this.showAlert(msg, 'danger');
        this.isUploading.set(false);
      },
    });
  }

  toggleBold() {
    const s = this.selected();
    if (s?.type === 'text') {
      const ts = s as TextElement;
      this.patchSelected({fontWeight: ts.fontWeight === 'bold' ? 'normal' : 'bold'});
    }
  }

  toggleItalic() {
    const s = this.selected();
    if (s?.type === 'text') {
      const ts = s as TextElement;
      this.patchSelected({fontStyle: ts.fontStyle === 'italic' ? 'normal' : 'italic'});
    }
  }

  toggleUnderline() {
    const s = this.selected();
    if (s?.type === 'text') {
      const ts = s as TextElement;
      this.patchSelected({textDecoration: ts.textDecoration === 'underline' ? 'none' : 'underline'});
    }
  }

  deleteSelected() {
    const id = this.selectedId();
    if (!id) return;
    this.elements.update(list => list.filter(e => e.id !== id));
    this.selectedId.set(null);
  }


  // 10) Export/Import
  exportJson() {
    return {
      name: this.form.value.name,
      description: this.form.value.description,
      label: this.label(),
      elements: this.elements()
    };
  }

  importJson(data: any) {
    if (!data) return;
    if (data.label) {
      this.label.set(data.label);
    }
    if (data.elements && Array.isArray(data.elements)) {
      this.elements.set(data.elements);
    }
    if (data.name || data.description) {
      this.form.patchValue({
        name: data.name ?? '',
        description: data.description ?? ''
      });
    }
    this.selectedId.set(null);
  }

  downloadJson() {
    const data = this.exportJson();
    const blob = new Blob([JSON.stringify(data, null, 2)], {type: 'application/json'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `label-${new Date().getTime()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  uploadJson(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = JSON.parse(e.target?.result as string);
          this.importJson(data);
          (event.target as HTMLInputElement).value = ''; // Reset input
        } catch (err) {
          console.error('Erro ao processar JSON', err);
          this.showAlert('Arquivo JSON inválido.', 'danger');
        }
      };
      reader.readAsText(file);
    }
  }

  protected readonly WebSafeFont = WebSafeFont;

  /** Placeholders disponíveis - use {{nome}} no texto. null/undefined mantém o placeholder; '' ou 0 exibem normalmente. */
  placeholderValues = signal<Record<string, string | number | null | undefined>>({});

  /** Exemplos de placeholders para etiquetas de produto */
  static readonly AVAILABLE_PLACEHOLDERS = [
    { key: 'productName', label: 'Nome do produto', example: '{{productName}}', defaultValue: 'Smart TV 4K UHD QLED Hisense 100" Polegadas - 100Q7QG' },
    { key: 'skuId', label: 'ID do SKU', example: '{{skuId}}', defaultValue: '1234' },
    { key: 'refId', label: 'Ref ID', example: '{{refId}}', defaultValue: 'HH100Q7QGPTOB' },
    { key: 'price', label: 'Preço', example: '{{price}}', defaultValue: 'R$ 1000,00' },
    { key: 'installments', label: 'Parcelas', example: '{{installments}}', defaultValue: '12' },
    { key: 'installmentValue', label: 'Valor da parcela', example: '{{installmentValue}}', defaultValue: 'R$ 83,33' },
    { key: 'totalInstallmentsValue', label: 'Total das parcelas', example: '{{totalInstallmentsValue}}', defaultValue: 'R$ 1000,00' },
    { key: 'brand', label: 'Marca', example: '{{brand}}', defaultValue: 'Hisense' },
    { key: 'category', label: 'Categoria', example: '{{category}}', defaultValue: 'Eletrônicos' },
  ] as const;

  protected readonly placeholders = TaggingLabelBuilderComponent.AVAILABLE_PLACEHOLDERS;

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('campaignId');
    const currentId = idParam ? Number(idParam) : null;

    if (currentId) {
      this.id.set(currentId);
      this.tagsService.getTagType(currentId).subscribe({
        next: (res) => {
          if (res.settings) {
            try {
              this.importJson(res.settings);
            } catch (err) {
              console.error('Erro ao processar JSON de configurações salvas', err);
              this.showAlert('Configurações salvas estão em formato inválido.', 'danger');
            }
          }
        },
        error: (err) => {
          this.showAlert(err?.error?.message ?? 'Erro ao carregar os dados.', 'danger');
        }
      });
    }

    this.setPlaceholderValue('productName', 'Smart TV 4K UHD QLED Hisense 100" Polegadas - 100Q7QG');
    this.setPlaceholderValue('skuId', '1234');
    this.setPlaceholderValue('refId', 'HH100Q7QGPTOB');
    this.setPlaceholderValue('price', '1000,00');
    this.setPlaceholderValue('installments', '12');
    this.setPlaceholderValue('installmentValue', '83,33');
    this.setPlaceholderValue('totalInstallmentsValue', '1000,00');
    this.setPlaceholderValue('brand', 'Hisense');
    this.setPlaceholderValue('category', 'Eletrônicos');
  }

  setPlaceholderValue(key: string, value: string | number | null | undefined): void {
    this.placeholderValues.update(m => ({ ...m, [key]: value === '' ? '' : value }));
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.alertMessage.set(null);
    this.isSaving.set(true);

    const { name, description } = this.form.value;
    const label = this.label();
    const payload: any = {
      name: name,
      description: description,
      width: label.widthMm,
      height: label.heightMm,
      settings: this.exportJson(),
      active: true
    };

    const currentId = this.id();
    if (currentId) {
      const updatePayload: ProductTagTypeCrudUpdateModel = {
        ...payload,
        id: currentId
      };
      this.tagsService.updateType(updatePayload).subscribe({
        next: () => {
          this.showAlert('Salvo com sucesso!', 'success');
          this.isSaving.set(false);
        },
        error: (err) => {
          this.showAlert(err?.error?.message ?? 'Erro ao salvar.', 'danger');
          this.isSaving.set(false);
        }
      });
    } else {
      const createPayload: ProductTagTypeCrudCreateModel = payload;
      this.tagsService.createType(createPayload).subscribe({
        next: (res) => {
          this.isSaving.set(false);
          if (res.id) {
            // Tenta redirecionar para a rota de edição se possível.
            // Como a rota exata não foi confirmada, vou usar um padrão comum
            // ou apenas recarregar se a rota atual mudar.
            this.router.navigate(['/tagging/campaign', res.id]);
          }
        },
        error: (err) => {
          this.showAlert(err?.error?.message ?? 'Erro ao criar.', 'danger');
          this.isSaving.set(false);
        }
      });
    }
  }

  showAlert(message: string, type: 'success' | 'danger' | 'warning' | 'info' = 'info') {
    this.alertMessage.set(message);
    this.alertType.set(type);
    // Opcional: auto-dismiss depois de alguns segundos se for success
    if (type === 'success') {
      setTimeout(() => {
        if (this.alertMessage() === message) {
          this.alertMessage.set(null);
        }
      }, 5000);
    }
  }
}
